import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Gift, Users, Heart, MailCheck, Clock, RefreshCw, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const followupLabel = (d) => {
  if (d.followup_opt_out) return { text: "Unsubscribed", cls: "bg-muted text-muted-foreground" };
  if (d.followup_error) return { text: "Follow-up failed", cls: "bg-red-100 text-red-700" };
  if (d.followup_sent) return { text: "Follow-up sent", cls: "bg-green-100 text-green-800" };
  if (d.followup_due_ts) return { text: `Due ${fmtDate(new Date(d.followup_due_ts * 1000).toISOString())}`, cls: "bg-amber-100 text-amber-800" };
  return { text: "No follow-up", cls: "bg-muted text-muted-foreground" };
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="p-5 bg-white rounded-2xl border border-border/40 h-full flex flex-col" data-testid={testId}>
    <div className="flex items-start gap-2 text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium min-h-[2rem]">
      <Icon className="h-4 w-4 text-primary flex-shrink-0" /> {label}
    </div>
    <p className="mt-auto pt-2 font-serif text-3xl font-semibold text-foreground tabular-nums">{value}</p>
  </Card>
);

const FollowupBadge = ({ d }) => {
  const f = followupLabel(d);
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${f.cls}`}>{f.text}</span>;
};

export const WordOfMouthDashboard = ({ adminPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/word-of-mouth`, { params: { password: adminPassword } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.stats || {};
  return (
    <div className="space-y-8" data-testid="word-of-mouth-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Everyone who shared your guide or group program with a friend, plus people who claimed the guide for themselves. Each guide recipient gets one gentle follow-up 3 days later.
        </p>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-full" data-testid="wom-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard icon={Gift} label="Guide gifts" value={s.guide_gifts ?? "—"} testId="wom-stat-guide-gifts" />
        <StatCard icon={Users} label="Group invites" value={s.group_invites ?? "—"} testId="wom-stat-group-invites" />
        <StatCard icon={Heart} label="Self claims" value={s.guide_claims ?? "—"} testId="wom-stat-guide-claims" />
        <StatCard icon={Sparkles} label="People sharing" value={s.unique_senders ?? "—"} testId="wom-stat-unique-senders" />
        <StatCard icon={MailCheck} label="Follow-ups sent" value={s.followups_sent ?? "—"} testId="wom-stat-followups-sent" />
        <StatCard icon={Clock} label="Follow-ups due" value={s.followups_pending ?? "—"} testId="wom-stat-followups-pending" />
      </div>

      <Card className="bg-white rounded-2xl overflow-hidden" data-testid="wom-invites-table">
        <div className="px-5 py-4 border-b border-border/40 font-serif text-lg font-semibold">Friend shares</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Shared by</TableHead>
              <TableHead>Sent to</TableHead>
              <TableHead>Page</TableHead>
              <TableHead>Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.invites || []).map((d) => (
              <TableRow key={d.id} data-testid={`wom-invite-row-${d.id}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(d.timestamp)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="rounded-full">{d.kind === "relationship-guide" ? "Guide gift" : "Group invite"}</Badge>
                </TableCell>
                <TableCell className="font-medium">{d.sender_name}</TableCell>
                <TableCell>
                  {d.friend_name && <span className="block">{d.friend_name}</span>}
                  <span className="text-xs text-muted-foreground">{d.friend_email}</span>
                  {!d.email_sent && <span className="block text-xs text-red-600">Email failed</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{d.page_path}</TableCell>
                <TableCell>{d.kind === "relationship-guide" ? <FollowupBadge d={d} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
              </TableRow>
            ))}
            {data && !data.invites.length && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10" data-testid="wom-invites-empty">No friend shares yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Card className="bg-white rounded-2xl overflow-hidden" data-testid="wom-claims-table">
        <div className="px-5 py-4 border-b border-border/40 font-serif text-lg font-semibold">Guide claimed for themselves</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Page</TableHead>
              <TableHead>Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.guide_claims || []).map((d) => (
              <TableRow key={d.id} data-testid={`wom-claim-row-${d.id}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(d.timestamp)}</TableCell>
                <TableCell className="font-medium">{d.first_name || "—"}</TableCell>
                <TableCell className="text-sm">{d.email}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{d.page_path || "/"}</TableCell>
                <TableCell><FollowupBadge d={d} /></TableCell>
              </TableRow>
            ))}
            {data && !data.guide_claims.length && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10" data-testid="wom-claims-empty">No guide claims yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
